import React from 'react';
import {
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import ActionDialogShell from './ActionDialogShell';

export default function RejectDialog({
    open,
    onClose,
    rejectReason,
    setRejectReason,
    rejectReasonExtra,
    setRejectReasonExtra,
    rejectionReasons,
    isConfigLoading,
    isPending,
    isArticleBodyLoading,
    isError,
    onReject,
}) {
    const theme = useTheme();
    const intl = useIntl();

    return (
        <ActionDialogShell
            open={open}
            onClose={onClose}
            title={intl.formatMessage({ id: 'newsQueue.dialog.reject.title' })}
            headerBgColor={theme.palette.error.main}
            headerTextColor={theme.palette.error.contrastText}
            actions={
                <>
                    <Button disabled={isPending} onClick={onClose}>
                        {intl.formatMessage({ id: 'newsQueue.common.cancel' })}
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        disabled={
                            !rejectReason ||
                            isConfigLoading ||
                            (rejectReason === 'other' && !rejectReasonExtra.trim()) ||
                            isPending ||
                            isArticleBodyLoading
                        }
                        onClick={onReject}
                    >
                        {intl.formatMessage({ id: 'newsQueue.actions.reject' })}
                    </Button>
                </>
            }
        >
            <FormControl sx={{ mt: 1, width: '100%' }}>
                <FormLabel id="reject-reason-label">
                    {intl.formatMessage({ id: 'newsQueue.reject.reasonLabel' })}
                </FormLabel>

                {isConfigLoading ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {intl.formatMessage({ id: 'newsQueue.reject.loadingReasons' })}
                    </Typography>
                ) : (
                    <RadioGroup
                        aria-labelledby="reject-reason-label"
                        name="reject-reason"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                    >
                        {rejectionReasons.map((reason) => (
                            <FormControlLabel
                                key={reason}
                                value={reason}
                                control={<Radio />}
                                label={reason}
                            />
                        ))}
                        <FormControlLabel
                            value="other"
                            control={<Radio />}
                            label={intl.formatMessage({ id: 'newsQueue.reject.otherReason' })}
                        />
                    </RadioGroup>
                )}
            </FormControl>

            <TextField
                autoFocus={rejectReason === 'other'}
                margin="dense"
                id="reason-extra"
                label={intl.formatMessage({ id: 'newsQueue.feedback.extraLabel' })}
                type="text"
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
                value={rejectReasonExtra}
                onChange={(e) => setRejectReasonExtra(e.target.value)}
                helperText={
                    rejectReason === 'other'
                        ? intl.formatMessage({ id: 'newsQueue.reject.otherReasonHelper' })
                        : intl.formatMessage({ id: 'newsQueue.feedback.optionalHelper' })
                }
            />

            {isError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {intl.formatMessage({ id: 'newsQueue.reject.error.generic' })}
                </Typography>
            )}
        </ActionDialogShell>
    );
}
